<template>
	<div
		class="absolute bg-white z-10 cursor-pointer"
		:style="{
			width: `${barWidth}px`,
			height: `${barHeight}px`,
			zIndex: i,
			top: `${barTop === 0 ? barTop : barTop + i * barHeight}px`,
			left: `${barLeft === 0 ? barLeft : contentWidth + i * barWidth}px`,
		}"
	>
		<div class="w-full h-full relative grid">
			<!-- Background Color -->
			<div
				class="absolute top-0 left-0 w-full md:w-[calc(100%-1px)] h-[calc(100%-1px)] md:h-full"
				:class="`bg-transparent-${apartment.color}`"
				@click="$emit('select')"
			></div>

			<!-- Apartment Name -->
			<div
				class="
					absolute top-1/2 left-1/2 z-20 flex justify-center items-center pointer-events-none
					md:-rotate-90 -translate-x-1/2 -translate-y-1/2
				"
				:style="{
					width: `${barTop === 0 ? barHeight : barWidth}px`,
					height: `${barTop === 0 ? barWidth : barHeight}px`,
				}"
			>
				<span
					class="uppercase font-display text-[30px] md:text-[40px]"
					:class="`text-${apartment.color}`"
				>
					{{ $t('apartments.' + apartment.color) }}
				</span>
			</div>

			<!-- Apartment Capacity -->
			<div
				v-if="apartment.capacity"
				class="
					absolute bottom-1/2 translate-y-1/2 w-[50px] md:translate-y-0 
					md:bottom-4 right-4 md:left-1/2 md:-translate-x-1/2 md:w-full z-20 
					flex justify-center items-center pointer-events-none
				"
			>
				<div class="w-full h-fit relative grid place-content-center">
					<div
						class="
							absolute
							left-0 -translate-x-full
							md:left-1/2 md:-translate-x-1/2
							top-1/2 -translate-y-1/2
							md:top-0 md:-translate-y-1/2
							w-fit h-fit -z-10 opacity-50
						"
					>
						<ProfileIcon :width="40" :height="40" color="white" />
					</div>
					<span
						class="uppercase font-display text-[20px] whitespace-nowrap"
						:class="`text-${apartment.color}`"
					>
						{{ apartment.capacity }}
					</span>
				</div>
			</div>

			<!-- Apartment Content -->
			<div
				ref="content"
				class="absolute bg-white"
				:class="`bg-${apartment.color}`"
				:style="{
					top: barTop === 0 ? 0 : '100%',
					left: barLeft === 0 ? 0 : '100%',
					width: `${contentWidth}px`,
					height: `${contentHeight}px`,
				}"
			>
				<div
					class="relative w-full h-full bg-opacity-50"
					:class="`bg-${apartment.color}`"
				>
					<div class="flex flex-col justify-center items-start gap-4 h-full p-2 md:p-4 lg:p-6 xl:p-8">
						<!-- CONTENT GOES HERE -->

						<p v-if="apartment.text" class="text-[17px] leading-[27px]">
							{{ $t(apartment.text) }}
						</p>

						<div v-if="apartment.services" class="w-full flex flex-wrap gap-2">
							<div
								v-for="service in apartment.services"
								:key="service"
								class="flex w-fit justify-start items-center gap-2 rounded p-1 md:p-2 pr-3 md:pr-4 border bg-white"
								:class="{
									[`border-${apartment.color}`]: apartment.color,
								}"
							>
								<component :is="service.icon" :width="20" :height="20" color="black" class="scale-[0.8] md:scale-[1]"/>
								<span class="text-xs md:text-sm font-normal text-black">
									{{ $t(service.name) }}
								</span>
							</div>
						</div>

						<div
							class="relative w-full h-2/5 md:h-3/5 grid place-content-center overflow-hidden"
							@mouseenter="startAnimation"
							@mouseleave="stopAnimation"
						>
							<div 
								class="
									cursor-pointer
									absolute top-0 left-0 w-full h-full z-0
									grid grid-rows-2 grid-cols-2 md:grid-cols-3 grid-flow-row
								"
								@click="globalStore.galleryImages = apartment.images"
							>
								<img 
									v-for="image in apartment.galleryPreview.slice(0, 4)"
									:key="image"
									:src="image"
									class="block md:hidden gallery-scale w-full h-full object-cover"
								/>
								<img 
									v-for="image in apartment.galleryPreview"
									:key="image"
									:src="image"
									class="hidden md:block gallery-scale w-full h-full object-cover"
								/>

								<div
									class="absolute top-0 left-0 w-full h-full crt z-10 opacity-50"
								></div>

								<div
									class="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_center,_#00000055,_transparent)] z-20"
								></div>
							</div>

							<div class="w-full grid z-20 cursor-pointer pointer-events-none">
								<span class="drop-shadow-[0_2px_2px_rgba(0,0,0,1)] gallery-scale capitalize font-display italic text-3xl lg:text-4xl text-white my-auto">
									{{ $t('global.gallery') }}
								</span>
							</div>
						</div>

						<div class="mx-auto w-2/5 max-w-64 min-w-52">
							<Btn
								class="p-2 w-24"
								:color="apartment.color"
								@click="scrollToRequest"
							>
								<span class="text-white text-md md:text-lg 2xl:text-xl font-medium">
									{{ $t('global.bookNow') }}
								</span>
							</Btn>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import gsap from 'gsap';
import { useGlobalStore } from '@/stores/global';
import Btn from '../form/Btn.vue';
import ProfileIcon from '../icons/ProfileIcon.vue';
import tailwindConfig from '../../../tailwind.config';

export default {
	name: 'ApartmentAboutBar',
	props: {
		apartment: {
			type: Object,
			required: true,
		},
		i: {
			type: Number,
			required: true,
		},
		barWidth: {
			type: Number,
			required: true,
		},
		barHeight: {
			type: Number,
			required: true,
		},
		barTop: {
			type: Number,
			required: true,
		},
		barLeft: {
			type: Number,
			required: true,
		},
		contentWidth: {
			type: Number,
			required: true,
		},
		contentHeight: {
			type: Number,
			required: true,
		},
	},
	components: {
		Btn,
		ProfileIcon,
	},
	setup() {
		return {
			globalStore: useGlobalStore(),
			tailwindConfig,
		};
	},
	data() {
		return {
			galleryOpen: false,
		};
	},
	methods: {
		startAnimation() {
			gsap.to('.gallery-scale', {
				scale: 1.2,
				duration: 1,
				ease: 'power2.out',
			});
		},
		stopAnimation() {
			gsap.to('.gallery-scale', {
				scale: 1,
				duration: 1,
				ease: 'power2.out',
			});
		},
		scrollToRequest() {
			if (!this.apartment.justGallery) {
				this.globalStore.selectedApartment = this.apartment.color;
			}

			const request = document.getElementById('request');
			request.scrollIntoView({ behavior: 'smooth' });
		},
	}
};
</script>
